import React, { useState, useEffect, useCallback, useRef, useMemo  } from 'react';
import { BeatLoader } from 'react-spinners';
import '../../../../styles/infringement_graph.css';
import { url } from '../../../../api/endpointURL.js';
import HeatmapChart from './infringementHeatmap.js';
import { filterMonthlyDataByCamera,
        filterWeeklyDataByCamera, 
        filterDailyDataByCamera, 
        filterHeatmapDataByCamera,
        calculateTotalInfringementPercentage,
        checkForNoInfringementData
} from './infringementUtils.js';
import { getTotalForPeriod, getPercentageIncrease, getYearlyTotal } from '../baseLpr/baseLprUtils.js';
import MonthlyInfringementChart from './MonthlyInfringementGraph.js';
import WeeklyInfringementChart from './WeeklyInfringementGraph.js';
import DailyInfringementChart from './DailyInfringementGraph.js';
import TopTenInfringers from './TopTenInfringers.js';

//returns current week numbner
function getCurrentWeekNumber() {
    const currentDate = new Date();
    const startOfYear = new Date(currentDate.getFullYear(), 0, 0);
    const diff = currentDate - startOfYear;
    return Math.floor(diff / (7 * 24 * 60 * 60 * 1000));
}

const LprInfringementChart = ({ 
    hwid,
    selectedCameraId,
    selectedYear,
    handleInfringementDataState,
}) => {
    
    //temporarily hardcoding hwid while using test data from the dev database. 
    const fakeHwid = '99616c51-3ec5-4852-b973-f269504052c1';

    //might need to come back here to check if it is getting the right month and when I confirm that we are just using calendar year instead of financial yeat. 
    const [monthlyInfringementData, setMonthlyInfringementData] = useState([]);
    const [weeklyInfringementData, setWeeklyInfringementData] = useState([]);
    const [dailyInfringementData, setDailyInfringementData] = useState([]);
    const [heatmapData, setHeatmapData] = useState([]);
    const [topInfringersData, setTopInfringersData] = useState([]);
    const [prevYearMonthlyInfringementData, setPrevYearMonthlyInfringementData] = useState([]);
    const [totalVehicles, setTotalVehicles] = useState(0);

    const [monthlyInfringementDataLoading, setMonthlyInfringementDataLoading] = useState(false);
    const [weeklyInfringementDataLoading, setWeeklyInfringementDataLoading] = useState(false);
    const [dailyInfringementDataLoading, setDailyInfringementDataLoading] = useState(false);
    const [heatmapDataLoading, setHeatmapDataLoading] = useState(false);
    const [topInfringersLoading, setTopInfringersLoading] = useState(false);
    const [isTopOffendersOpen, setIsTopOffendersOpen] = useState(false);


    //selected month and week are numbers. 
    const [selectedMonth, setSelectedMonth] = useState(null);
    const [selectedWeek, setSelectedWeek] = useState(null);
    const [selectedDate, setSelectedDate] = useState(null);
    const [selectedDayOfWeek, setSelectedDayOfWeek] = useState(null);
    const [selectedWeekStartDate, setSelectedWeekStartDate] = useState();

    // Has the user manually selected a month, week, or day?
    const [monthSelectedByUser, setMonthSelectedByUser] = useState(false);
    const [weekSelectedByUser, setWeekSelectedByUser] = useState(false);
    const [dateSelectedByUser, setDateSelectedByUser] = useState(false);

    const [topTenExpanded, setTopTenExpanded] = useState(false);

    //Prevent race conditions caused by outdated fetches
    const monthlyAbortControllerRef = useRef(null);
    const prevYearMonthlyAbortControllerRef = useRef(null);
    const weeklyAbortControllerRef = useRef(null);
    const dailyAbortControllerRef = useRef(null);
    const heatmapAbortControllerRef = useRef(null);
    const topTenAbortControllerRef = useRef(null);
    const totalVehiclesControllerRef = useRef(null);
    
    const monthlyRequestIdRef = useRef(0);
    const prevYearMonthlyRequestIdRef = useRef(0);
    const weeklyRequestIdRef = useRef(0);
    const dailyRequestIdRef = useRef(0);
    const heatmapRequestIdRef = useRef(0);
    const topTenRequestIdRef = useRef(0);

    // DATA FETCHING STUFF
    const aggregatedMonthlyData = useMemo(() =>
        filterMonthlyDataByCamera(monthlyInfringementData, selectedCameraId),
        [monthlyInfringementData, selectedCameraId]
    );

    const prevYearFilteredMonthlyData = useMemo(() => 
        filterMonthlyDataByCamera(prevYearMonthlyInfringementData, selectedCameraId),
        [prevYearMonthlyInfringementData, selectedCameraId]
    );

    // Aggregate weekly data
    const aggregatedWeeklyData = useMemo(() =>
        filterWeeklyDataByCamera(weeklyInfringementData, selectedCameraId),
        [weeklyInfringementData, selectedCameraId]
    );

    const aggregatedDailyData = useMemo(() =>
        filterDailyDataByCamera(dailyInfringementData, selectedCameraId),
        [dailyInfringementData, selectedCameraId]
    );

    const aggregatedHeatmapData = useMemo(() => 
        filterHeatmapDataByCamera(heatmapData, selectedCameraId),
        [heatmapData, selectedCameraId]
    );

    const totalInfringements = useMemo(() => 
        getYearlyTotal(monthlyInfringementData, 'total_infringements'),
        [monthlyInfringementData]
    );

    const infringementPercentage = useMemo(() => 
        calculateTotalInfringementPercentage(totalVehicles, totalInfringements),
        [totalVehicles, totalInfringements]
    );

    //Hide component when no data
    const [initialFetchCompleted, setInitialFetchCompleted] = useState(false);
    const [showInfringement, setShowInfringement] = useState(true);
    const [isInfringementExpanded, setIsInfringementExpanded] = useState(true);

    const prevCameraRef = useRef(selectedCameraId);

    //----------------------------------------------------

    useEffect(() => {
        const cameraChanged = prevCameraRef.current !== selectedCameraId;
        prevCameraRef.current = selectedCameraId;

        if (cameraChanged) {
            setInitialFetchCompleted(false);
        }
    }, [selectedCameraId]);

    useEffect(() => {
        if (aggregatedMonthlyData.length) {
            setInitialFetchCompleted(true);
        }
    }, [aggregatedMonthlyData]);

    const hasActualData = useCallback(() => {
        const hasData = checkForNoInfringementData(aggregatedMonthlyData);
        if (!hasData) {
            return false;
        } else {
            return true;
        }

    }, [aggregatedMonthlyData, selectedCameraId]);

    //----------------------------------------



    const fetchMonthlyInfringementData = useCallback(async () => {
        if (monthlyAbortControllerRef.current) {
            monthlyAbortControllerRef.current.abort();
        }

        monthlyAbortControllerRef.current = new AbortController();
        const { signal } = monthlyAbortControllerRef.current;

        monthlyRequestIdRef.current += 1;
        const currentRequestId = monthlyRequestIdRef.current;
        try {
            setMonthlyInfringementDataLoading(true);
            const response = await fetch(`${url}/lprInfringement/monthlyInfringements?year=${selectedYear}&hwid=${hwid}`, { signal });
            if (!response.ok) {
                throw new Error(`Server responded with status: ${response.status}`);
            }

            const responseData = await response.json();
            
            setMonthlyInfringementDataLoading(false);
            
            // console.log("monthlyInfringement data fetched from the server: ", responseData);
            setMonthlyInfringementData(responseData);

            setInitialFetchCompleted(true);
            
            if (responseData.length) {
                const latestMonthWithData = [...responseData]
                    .reverse()
                    .find(month => month.total_infringements > 0);

                if (latestMonthWithData) {
                    setSelectedMonth(latestMonthWithData.month);
                }
            }

        } catch (error) {
            if (error.name === 'AbortError') {
                // console.log("Fetch monthly infringement data aborted");
                return;
            }
            console.error('Error fetching monthly infringment data: ', error);
        } finally {
            if (currentRequestId === monthlyRequestIdRef.current) {
                setMonthlyInfringementDataLoading(false);
            }
        }
    }, [selectedYear, hwid]);

    const fetchMonthlyInfringementDataPreviousYear = useCallback(async () => {
        if (prevYearMonthlyAbortControllerRef.current) {
            prevYearMonthlyAbortControllerRef.current.abort();
        }

        prevYearMonthlyAbortControllerRef.current = new AbortController();
        const { signal } = prevYearMonthlyAbortControllerRef.current;

        prevYearMonthlyRequestIdRef.current += 1;
        const currentRequestId = prevYearMonthlyRequestIdRef.current;

        try {
            setMonthlyInfringementDataLoading(true);
            const response = await fetch(`${url}/lprInfringement/monthlyInfringements?year=${selectedYear - 1}&hwid=${hwid}`, { signal });
            if (!response.ok) {
                throw new Error(`Server responded with status: ${response.status}`);
            }
            const responseData = await response.json();
            
            setMonthlyInfringementDataLoading(false);
            
            // console.log("monthlyInfringement data fetched from the server: ", responseData);
            if (currentRequestId === prevYearMonthlyRequestIdRef.current) {
                setPrevYearMonthlyInfringementData(responseData);
            }
            

        } catch (error) {
            if (error.name === 'AbortError') {
                // console.log("fetching prev year monthly infringment data aborted");
            }
            console.error('Error fetching monthly infringment data: ', error);
        } finally {
            if (currentRequestId === prevYearMonthlyRequestIdRef.current) {
                setMonthlyInfringementDataLoading(false);
            }
        }
    }, [selectedYear, hwid]);

    const fetchWeeklyInfringementData = useCallback(async (monthNumber) => {
        if (monthNumber === null) {
            // console.log("A month has not been selected!!!");
            return [];
        }

        if (weeklyAbortControllerRef.current) {
            weeklyAbortControllerRef.current.abort();
        }

        weeklyAbortControllerRef.current = new AbortController();
        const { signal } = weeklyAbortControllerRef.current;

        weeklyRequestIdRef.current += 1;
        const currentRequestId = weeklyRequestIdRef.current;
        try {

            setWeeklyInfringementDataLoading(true);

            const response = await fetch(`${url}/lprInfringement/weeklyInfringements?year=${selectedYear}&month=${monthNumber}&hwid=${hwid}`, { signal });
            if (!response.ok) {
                throw new Error(`Server responded with status: ${response.status}`);
            }

            const responseData = await response.json();

            setWeeklyInfringementDataLoading(false);

            if (currentRequestId === weeklyRequestIdRef.current) {
                setWeeklyInfringementData(responseData);
            }

            if (responseData.length) {
                const latestWeekWithData = [...responseData]
                    .reverse()
                    .find(week => week.total_infringements > 0);

                if (latestWeekWithData) {
                    setSelectedWeek(latestWeekWithData.week);
                    setSelectedWeekStartDate(latestWeekWithData.week_start_date);
                }
                
            }

        } catch (error) {
            if (error.name === 'AbortError') {
                // console.log("fetching weekly infringement data aborted");
                return;
            }
            console.error('Error fetching monthly infringment data: ', error);
        } finally {
            if (currentRequestId === weeklyRequestIdRef.current) {
                setMonthlyInfringementDataLoading(false);
            }
        }
    }, [selectedYear, hwid, selectedMonth, monthlyInfringementData]);

    const fetchDailyInfringementData = useCallback(async (weekNumber) => {
        if (weekNumber === null) {
            // console.log("A week has not been selected!!!");
            return [];
        }

        if (dailyAbortControllerRef.current) {
            dailyAbortControllerRef.current.abort();
        }

        dailyAbortControllerRef.current = new AbortController();
        const { signal } = dailyAbortControllerRef.current;

        dailyRequestIdRef.current += 1;
        const currentRequestId = dailyRequestIdRef.current;


        try {
            setDailyInfringementDataLoading(true);

            const response = await fetch(`${url}/lprInfringement/dailyInfringements?year=${selectedYear}&month=${selectedMonth}&week=${weekNumber}&hwid=${hwid}`, { signal });
            if (!response.ok) {
                throw new Error(`Server responded with status: ${response.status}`);
            }

            const responseData = await response.json();

            if (currentRequestId === dailyRequestIdRef.current) {
                setDailyInfringementData(responseData);
            }
            if (responseData.length) {
                const latestDayWithData = [...responseData]
                    .reverse()
                    .find(day => day.daily_infringements > 0);
                

                if (latestDayWithData) {
                    setSelectedDate(latestDayWithData.day_number);
                    setSelectedDayOfWeek(latestDayWithData.day);
                }
            }


        } catch (error) {
            if (error.name === "AbortError") {
                // console.log("daily infringment data fetch aborted for week: ", weekNumber);
            } else {
                console.error('Error fetching daily infringment data: ', error);
                setDailyInfringementDataLoading(false);
            }
        } finally {
            if (currentRequestId === dailyRequestIdRef.current) {
                setDailyInfringementDataLoading(false);
            }
        }

    }, [selectedYear, selectedMonth, selectedWeek, hwid]);

    const fetchHeatmapData = useCallback(async (weekNumber) => {
        if (weekNumber === null) {
            // console.log("A week has not been selected!!!");
            return [];
        }

        if (heatmapAbortControllerRef.current) {
            heatmapAbortControllerRef.current.abort();
        }

        heatmapAbortControllerRef.current = new AbortController();
        const { signal } = heatmapAbortControllerRef.current;

        heatmapRequestIdRef.current += 1;
        const currentRequestId = heatmapRequestIdRef.current;
        try {
            setHeatmapDataLoading(true);
            const response = await fetch(`${url}/lprInfringement/dailyInfringementsHeatmap?year=${selectedYear}&month=${selectedMonth}&week=${weekNumber}&hwid=${hwid}`, {signal});
            if (!response.ok) {
                throw new Error(`Server responded with status: ${response.status}`);
            }

            const responseData = await response.json();

            if (currentRequestId === heatmapRequestIdRef.current) {
                setHeatmapData(responseData);
            }

        } catch (error) {
            if (error.name === "AbortError") {
                // console.log("fetching heatmap data aborted.");
            } else {
                console.error('Error fetching daily infringment heatmap data: ', error);
                setHeatmapDataLoading(false);
            }
        } finally {
            if (currentRequestId === heatmapRequestIdRef.current) {
                setHeatmapDataLoading(false);
            }
        }
    }, [selectedYear, selectedMonth, selectedWeek, hwid]);

    const fetchTopInfringersForMonth = useCallback(async () => {
        if (topTenAbortControllerRef.current) {
            topTenAbortControllerRef.current.abort();
        }

        topTenAbortControllerRef.current = new AbortController();
        const { signal } = topTenAbortControllerRef.current;

        topTenRequestIdRef.current += 1;
        const currentRequestId = topTenRequestIdRef.current;
        try {
            setTopInfringersLoading(true);
            const response = await fetch(`${url}/lprInfringement/topInfringersThisMonth?year=${selectedYear}&month=${selectedMonth}&hwid=${hwid}`, { signal });
            if (!response.ok) {
                throw new Error(`Server responded with status: ${response.status}`);
            }

            const responseData = await response.json();
            if (currentRequestId === topTenRequestIdRef.current) {
                setTopInfringersData(responseData);
            }

        } catch (error) {
            if (error.name === 'AbortError') {
                // console.log("fetching top ten infringers aborted");
            }
            console.error('Error fetching monthly infringment data: ', error);
        } finally {
            if (currentRequestId === topTenRequestIdRef.current) {
                setMonthlyInfringementDataLoading(false);
            }
        }
    }, [selectedYear, selectedMonth, hwid]);

    const fetchTotalVehicles = useCallback(async () => {
        if (totalVehiclesControllerRef.current) {
            totalVehiclesControllerRef.current.abort();
        }

        totalVehiclesControllerRef.current = new AbortController();
        const { signal } = totalVehiclesControllerRef.current;
        try {
            const response = await fetch(`${url}/lprCount/monthlyLprCount?year=${selectedYear}&hwid=${hwid}`, { signal });
            if (!response.ok) {
                throw new Error(`Server responded with status: ${response.status}`);
            }

            const responseData = await response.json();
            
            let total = 0;

            responseData.forEach(month => {
                if (month.total_count) {
                    const count = parseInt(month.total_count);
                    const safeCount = isNaN(count) ? 0 : count;
                    total += safeCount;
                }
            });

            
            setTotalVehicles(total);

        } catch (e) {
            if (e.name === 'AbortError') {
                // console.log('Fetching total vehicles aborted');
            }
            // console.log(e)
        }
    }, [hwid]);

    //------------------------------------------------------------------------

    useEffect(() => {
        // console.log("resetting abort controllers");
        return () => {
            if (monthlyAbortControllerRef.current) monthlyAbortControllerRef.current.abort();
            if (weeklyAbortControllerRef.current) weeklyAbortControllerRef.current.abort();
            if (dailyAbortControllerRef.current) dailyAbortControllerRef.current.abort();
            if (heatmapAbortControllerRef.current) heatmapAbortControllerRef.current.abort();
            if (topTenAbortControllerRef.current) topTenAbortControllerRef.current.abort();
            if (totalVehiclesControllerRef.current) totalVehiclesControllerRef.current.abort();
        };
    }, []);


    useEffect(() => {
        // console.log("checking to see if initial infringment fetch has completed>>>>>>>>>>>>>>>>> ", initialFetchCompleted);
        if (initialFetchCompleted) {
            const hasData = hasActualData();
            // console.log("Initial fetch completed, has data >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>> ", hasData);
            // console.log("current aggregatedMonthlyData: ", aggregatedMonthlyData);
            setShowInfringement(hasData);
            setIsInfringementExpanded(hasData);

            if (handleInfringementDataState) {
                // console.log("Notifying parent about data state: ", hasData);
                handleInfringementDataState(hasData);
            }
        }
    }, [initialFetchCompleted, hasActualData, handleInfringementDataState]);

    useEffect(() => {
        if (selectedYear && hwid) {
            fetchMonthlyInfringementData();
        }
    }, [selectedYear, hwid, fetchMonthlyInfringementData]);

    useEffect(() => {
        if (selectedYear && hwid) {
            fetchTotalVehicles();
        }
    }, [selectedYear, hwid]);

    // If a month has not been selected then select the latest month.
    useEffect(() => {
        if (!monthSelectedByUser && monthlyInfringementData.length) {
            const latestMonthWithData = [...monthlyInfringementData]
                .reverse()
                .find(month => month.total_infringements > 0);

            if (latestMonthWithData) {
                setSelectedMonth(latestMonthWithData.month);
            }
        }
    }, [aggregatedMonthlyData, monthSelectedByUser]);

    // Fetch the weekly infringement data. 
    useEffect(() => {
        if (monthlyInfringementData.length) {
            fetchWeeklyInfringementData(selectedMonth);
        }
    }, [monthlyInfringementData.length, hwid, selectedYear, selectedMonth, fetchWeeklyInfringementData]);

    useEffect(() => {
        if (selectedMonth) {
            fetchTopInfringersForMonth();
        }
    }, [selectedYear, selectedMonth, hwid, monthlyInfringementData, fetchTopInfringersForMonth]);



    useEffect(() => {
        const week = weeklyInfringementData.find(d => d.week === selectedWeek);
        if (week) {
            setSelectedWeekStartDate(week.week_start_date);
        }
    }, [selectedWeek]);

    // Fetch daily infringement data. 
    useEffect(() => {
        
        if (weeklyInfringementData.length) {
            fetchDailyInfringementData(selectedWeek);
        }
    }, [weeklyInfringementData, hwid, selectedYear, selectedMonth, selectedWeek, fetchDailyInfringementData]);

    useEffect(() => {
        if (weeklyInfringementData.length) {
            fetchHeatmapData(selectedWeek);
        }
    }, [selectedYear, selectedMonth, selectedWeek, hwid, weeklyInfringementData, fetchHeatmapData]);

    const handleMonthlyBarClick = (monthNumber) => {
        setSelectedMonth(monthNumber);
        fetchWeeklyInfringementData(monthNumber);
    };

    const handleWeeklyBarClick = (weekNumber) => {
        setSelectedWeek(weekNumber);
        fetchDailyInfringementData(weekNumber);
    };

    const handleDailyBarClick = (day_number, dayName) => {
        setSelectedDate(day_number);
        setSelectedDayOfWeek(dayName);
    }

    const toggleTopTenExpanded = () => {
        setTopTenExpanded(!topTenExpanded);
    };


    return (
        <>
                {/* <div style={{ width: '100%', margin: '20px 0', textAlign: 'left', position: 'relative', cursor: 'pointer' }}
                    onClick={() => setIsInfringementExpanded(!isInfringementExpanded)}>
                    <div style={{ display: 'inline-block', padding: '0 10px', background: '#2B3441', position: 'relative', zIndex: 1, boxShadow: `0px 5px 15px ${isInfringementExpanded ? 'rgba(255, 165, 0, 0.2)' : 'rgba(255, 255, 255, 0.2)'}` }}>
                        <span style={{ fontSize: '1.2em', color: showInfringement ? 'white' : 'grey' }}>
                            Infringement Analytics {isInfringementExpanded ? '▲' : '▼'}
                        </span>
                    </div>
                    <div style={{ position: 'absolute', top: '50%', left: '0', right: '0', height: '0px', background: '#2B3441', boxShadow: `0px 5px 15px ${isInfringementExpanded ? 'rgba(255, 165, 0, 0.2)' : 'rgba(255, 255, 255, 0.2)'}`, zIndex: 0 }} />
                </div> */}
                {
                    (showInfringement && isInfringementExpanded) ?
                        <>
                            <div style={{ maxWidth: '100%', boxSizing: 'border-box' }}>
                            <div className="chart-total-container">
                            {/* Total Vehicles */}
                            <div className="chart-total-pill wide" style={{ alignSelf: isTopOffendersOpen ? 'flex-start' : 'stretch' }}>
                                <div className="chart-total-label">Total Vehicles {selectedYear}</div>
                                <div className="chart-total-value">{totalVehicles}</div>
                            </div>

                            {/* Total Infringements */}
                            <div className="chart-total-pill wide" style={{ alignSelf: isTopOffendersOpen ? 'flex-start' : 'stretch' }}>
                                <div className="chart-total-label">Total Infringements {selectedYear}</div>
                                <div className="chart-total-value">{totalInfringements}</div>
                            </div>

                            {/* Infringement Percentage */}
                            <div className="chart-total-pill wide" style={{ alignSelf: isTopOffendersOpen ? 'flex-start' : 'stretch' }}>
                                <div className="chart-total-label">Infringement % this year</div>
                                <div className="chart-total-value">
                                {monthlyInfringementDataLoading
                                    ? <BeatLoader color={'white'} size={10} />
                                    : `${infringementPercentage}%`}
                                </div>
                            </div>

                            {/* Top 10 Yearly Offenders - Collapsible */}
                            <div className="chart-total-pill" style={{
                                flexDirection: 'column',
                                alignItems: 'flex-start',
                                padding: '1rem',
                                width: '100%'
                            }}>
                                <div 
                                className="chart-total-label" 
                                style={{ 
                                    marginBottom: '0.5rem',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    width: '100%'
                                }}
                                onClick={() => setIsTopOffendersOpen(!isTopOffendersOpen)}
                                >
                                <span>Top 10 Yearly Offenders</span>
                                <span style={{ 
                                    transform: isTopOffendersOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                                    transition: 'transform 0.3s ease',
                                    fontSize: '0.8rem'
                                }}>
                                    ▼
                                </span>
                                </div>
                                
                                {isTopOffendersOpen && (
                                <div className="top-10-list" style={{
                                    width: '100%',
                                    borderTop: '1px solid rgba(255, 255, 255, 0.3)',
                                    paddingTop: '0.5rem',
                                    animation: 'slideDown 0.3s ease-out'
                                }}>
                                    <div style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    fontWeight: 'bold',
                                    marginBottom: '0.5rem'
                                    }}>
                                    <div>Plate</div>
                                    <div>Count</div>
                                    </div>
                                    {topInfringersData.map(offender => (
                                    <div key={offender.id} style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        padding: '0.25rem 0',
                                        borderBottom: '1px solid rgba(255,255,255,0.1)'
                                    }}>
                                        <div>{offender.plate}</div>
                                        <div>{offender.infringement_count}</div>
                                    </div>
                                    ))}
                                </div>
                                )}
                            </div>
                            </div>
                            <br />
                                <div className='lpr-chart-container'>
                                    <div id='lpr-bar-chart-wrapper'>
                                        <MonthlyInfringementChart
                                            selectedYear={selectedYear}
                                            monthlyInfringementData={aggregatedMonthlyData}
                                            monthlyDataLoading={monthlyInfringementDataLoading}
                                            selectedMonth={selectedMonth}
                                            handleMonthlyBarClick={handleMonthlyBarClick}
                                            totalMonthlyInfringements={getTotalForPeriod(aggregatedMonthlyData, selectedMonth, 'month', 'total_infringements')}
                                            monthlyInfringementPercentageIncrease={getPercentageIncrease(aggregatedMonthlyData, selectedMonth, 'month', 'total_infringements')}
                                        />
                                    </div>


                                    <div id='lpr-bar-chart-wrapper' >
                                        <WeeklyInfringementChart
                                            selectedYear={selectedYear}
                                            weeklyInfringementData={aggregatedWeeklyData}
                                            weeklyDataLoading={weeklyInfringementDataLoading}
                                            selectedMonth={selectedMonth}
                                            selectedWeek={selectedWeek}
                                            handleWeeklyBarClick={handleWeeklyBarClick}
                                            totalWeeklyInfringements={getTotalForPeriod(aggregatedWeeklyData, selectedWeek, 'week', 'total_infringements')}
                                            weeklyPercentageIncrease={getPercentageIncrease(aggregatedWeeklyData, selectedWeek, 'week', 'total_infringements')}
                                        />
                                    </div>

                                    <div id='lpr-bar-chart-wrapper'>
                                        <DailyInfringementChart
                                            dailyInfringementData={aggregatedDailyData}
                                            dailyDataLoading={dailyInfringementDataLoading}
                                            selectedDate={selectedDate}
                                            selectedDayOfWeek={selectedDayOfWeek}
                                            selectedWeekStartDate={selectedWeekStartDate}
                                            selectedMonth={selectedMonth}
                                            totalDailyInfringements={getTotalForPeriod(aggregatedDailyData, selectedDate, 'day_number', 'daily_infringements')}
                                            dailyInfringementPercentageIncrease={getPercentageIncrease(aggregatedDailyData, selectedDate, 'day_number', 'daily_infringements')}
                                        />
                                    </div>

                                </div>

                                <div className="lpr-heatmap-chart-container">
                                    <div className={heatmapDataLoading ? 'lpr-daily-bar-chart-container-loading' : ''} style={{
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        width: "100%",
                                        minHeight: "400px"
                                    }}>
                                        <span className='graph-title'>Infringement times this week</span>
                                        <HeatmapChart heatmapData={aggregatedHeatmapData} />
                                    </div>
                                </div>
                            </div>
                        </>

                        :
                        <div style={{ display: 'none' }}>

                        </div>
                }
        </>
    );

};

export default LprInfringementChart;